import React, { useEffect, useMemo, useState } from 'react';
import { ShieldCheck, X } from 'lucide-react';
import QRCode from 'qrcode';
import { API_ENDPOINTS } from '../../constants/api';
import { apiFetch } from '../../utils/apiClient';

const normalizeError = async (response, fallback) => {
  try {
    const payload = await response.json();
    const detail = String(payload?.detail || '').trim();
    if (detail) return detail;
  } catch (_err) {
    // no-op
  }
  return fallback;
};

export const MFAManagementModal = ({ isOpen, onClose, onMfaChanged }) => {
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [setupSecret, setSetupSecret] = useState('');
  const [setupUri, setSetupUri] = useState('');
  const [setupQrDataUrl, setSetupQrDataUrl] = useState('');
  const [setupQrError, setSetupQrError] = useState('');
  const [setupCode, setSetupCode] = useState('');
  const [actionCode, setActionCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const hasSetupSecret = Boolean(setupSecret);
  const isEnabled = Boolean(status?.mfa_enabled);
  const recoveryRemaining = Number(status?.recovery_codes_remaining || 0);

  const clearTransientState = () => {
    setSetupSecret('');
    setSetupUri('');
    setSetupQrDataUrl('');
    setSetupQrError('');
    setSetupCode('');
    setActionCode('');
  };

  const loadStatus = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await apiFetch(API_ENDPOINTS.AUTH_MFA_STATUS);
      if (!response.ok) {
        throw new Error(await normalizeError(response, 'Failed to load MFA status'));
      }
      const payload = await response.json();
      setStatus(payload);
    } catch (err) {
      setStatus(null);
      setError(String(err?.message || 'Failed to load MFA status'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    setMessage('');
    setRecoveryCodes([]);
    clearTransientState();
    loadStatus();
  }, [isOpen]);

  useEffect(() => {
    if (!setupUri) {
      setSetupQrDataUrl('');
      setSetupQrError('');
      return;
    }

    let cancelled = false;
    setSetupQrDataUrl('');
    setSetupQrError('');
    QRCode.toDataURL(setupUri, {
      errorCorrectionLevel: 'M',
      margin: 1,
      width: 220,
    })
      .then((dataUrl) => {
        if (!cancelled) {
          setSetupQrDataUrl(dataUrl);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setSetupQrError('Unable to render QR right now. Use the secret key below.');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [setupUri]);

  const startSetup = async () => {
    setLoading(true);
    setError('');
    setMessage('');
    setRecoveryCodes([]);
    try {
      const response = await apiFetch(API_ENDPOINTS.AUTH_MFA_SETUP_START, {
        method: 'POST',
      });
      if (!response.ok) {
        throw new Error(await normalizeError(response, 'Failed to start MFA setup'));
      }
      const payload = await response.json();
      setSetupSecret(String(payload?.secret || '').trim());
      setSetupUri(String(payload?.otpauth_uri || '').trim());
      setMessage('Scan the secret in your authenticator app, then enter a 6-digit code.');
    } catch (err) {
      setError(String(err?.message || 'Failed to start MFA setup'));
    } finally {
      setLoading(false);
    }
  };

  const confirmSetup = async () => {
    if (!setupSecret || !setupCode.trim()) {
      setError('Enter the MFA code from your authenticator app.');
      return;
    }
    setLoading(true);
    setError('');
    setMessage('');
    try {
      const response = await apiFetch(API_ENDPOINTS.AUTH_MFA_SETUP_CONFIRM, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          secret: setupSecret,
          code: setupCode.trim(),
        }),
      });
      if (!response.ok) {
        throw new Error(await normalizeError(response, 'Failed to confirm MFA setup'));
      }
      const payload = await response.json();
      setRecoveryCodes(Array.isArray(payload?.recovery_codes) ? payload.recovery_codes : []);
      setMessage('MFA enabled. Save your recovery codes in a secure location.');
      clearTransientState();
      await loadStatus();
      onMfaChanged?.();
    } catch (err) {
      setError(String(err?.message || 'Failed to confirm MFA setup'));
    } finally {
      setLoading(false);
    }
  };

  const regenerateRecoveryCodes = async () => {
    if (!actionCode.trim()) {
      setError('Enter your current MFA code to regenerate recovery codes.');
      return;
    }
    setLoading(true);
    setError('');
    setMessage('');
    try {
      const response = await apiFetch(API_ENDPOINTS.AUTH_MFA_REGENERATE_RECOVERY, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: actionCode.trim() }),
      });
      if (!response.ok) {
        throw new Error(await normalizeError(response, 'Failed to regenerate recovery codes'));
      }
      const payload = await response.json();
      setRecoveryCodes(Array.isArray(payload?.recovery_codes) ? payload.recovery_codes : []);
      setActionCode('');
      setMessage('Recovery codes regenerated. Store these new codes securely.');
      await loadStatus();
    } catch (err) {
      setError(String(err?.message || 'Failed to regenerate recovery codes'));
    } finally {
      setLoading(false);
    }
  };

  const disableMfa = async () => {
    if (!actionCode.trim()) {
      setError('Enter your current MFA code to disable MFA.');
      return;
    }
    setLoading(true);
    setError('');
    setMessage('');
    try {
      const response = await apiFetch(API_ENDPOINTS.AUTH_MFA_DISABLE, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: actionCode.trim() }),
      });
      if (!response.ok) {
        throw new Error(await normalizeError(response, 'Failed to disable MFA'));
      }
      setRecoveryCodes([]);
      setActionCode('');
      clearTransientState();
      setMessage('MFA is now disabled for your account.');
      await loadStatus();
      onMfaChanged?.();
    } catch (err) {
      setError(String(err?.message || 'Failed to disable MFA'));
    } finally {
      setLoading(false);
    }
  };

  const modalTitle = useMemo(() => {
    if (!status) return 'Security';
    return isEnabled ? 'MFA Enabled' : 'Set Up MFA';
  }, [status, isEnabled]);

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="w-full max-w-2xl">
        <div className="rounded-2xl border border-slate-200 bg-white shadow-[0_28px_70px_rgba(2,6,23,0.25)]">
          <section className="relative overflow-hidden px-8 pb-8 pt-8 md:px-10 md:pb-10">
            <button
              onClick={onClose}
              className="absolute right-6 top-6 rounded-lg border border-slate-200 bg-white p-1.5 text-slate-500 shadow-sm transition-colors hover:bg-slate-50 hover:text-slate-800"
              aria-label="Close MFA dialog"
            >
              <X className="h-4 w-4" />
            </button>

            <div className="inline-flex items-center gap-2 rounded-full border border-indigo-100 bg-indigo-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-indigo-700">
              <ShieldCheck className="h-3.5 w-3.5" />
              Account Security
            </div>

            <h3 className="mt-4 text-2xl font-bold text-slate-900">{modalTitle}</h3>
            <p className="mt-1 text-sm text-slate-600">
              Protect your account with time-based one-time passwords and recovery codes.
            </p>

            {loading && <p className="mt-4 text-sm text-slate-500">Working...</p>}
            {error && (
              <div className="mt-4 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                {error}
              </div>
            )}
            {message && (
              <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
                {message}
              </div>
            )}

            {status && (
              <div className="mt-5 grid gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700 md:grid-cols-2">
                <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                  <p className="text-[11px] uppercase tracking-[0.08em] text-slate-500">MFA status</p>
                  <p className="mt-1 text-base font-semibold text-slate-900">
                    {isEnabled ? 'Enabled' : 'Disabled'}
                  </p>
                </div>
                <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                  <p className="text-[11px] uppercase tracking-[0.08em] text-slate-500">Recovery codes left</p>
                  <p className="mt-1 text-base font-semibold text-slate-900">{recoveryRemaining}</p>
                </div>
                {Boolean(status?.mfa_required) && (
                  <p className="rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2 text-indigo-800 md:col-span-2">
                    MFA is required for your role at login.
                  </p>
                )}
              </div>
            )}

            {!isEnabled && (
              <div className="mt-5 rounded-xl border border-slate-200 bg-white p-4">
                <p className="text-sm font-semibold text-slate-900">Set up authenticator</p>
                {!hasSetupSecret ? (
                  <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
                    <p className="text-sm text-slate-600">
                      Generate a QR code and scan it using your authenticator app.
                    </p>
                    <button
                      onClick={startSetup}
                      disabled={loading}
                      className="mt-3 inline-flex items-center rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm font-semibold text-indigo-900 transition-colors hover:bg-indigo-100 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      Generate QR Setup
                    </button>
                  </div>
                ) : (
                  <div className="mt-4 grid gap-4 md:grid-cols-[235px_minmax(0,1fr)]">
                    <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                      <p className="text-xs font-medium uppercase tracking-[0.08em] text-slate-500">
                        Scan QR
                      </p>
                      {setupQrDataUrl ? (
                        <div className="mt-2 flex justify-center rounded-lg border border-slate-200 bg-white p-2">
                          <img
                            src={setupQrDataUrl}
                            alt="MFA setup QR code"
                            className="h-44 w-44 rounded-md"
                          />
                        </div>
                      ) : (
                        <div className="mt-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-xs text-slate-500">
                          Generating QR code...
                        </div>
                      )}
                      {setupQrError && (
                        <div className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2">
                          <p className="text-xs text-amber-800">{setupQrError}</p>
                          <p className="mt-2 text-xs text-amber-800">Setup key fallback</p>
                          <code className="mt-1 block rounded-md border border-amber-200 bg-white px-2 py-2 text-sm text-amber-900">
                            {setupSecret}
                          </code>
                        </div>
                      )}
                    </div>

                    <div className="flex flex-col gap-3">
                      <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600">
                        <p className="font-medium text-slate-800">Next step</p>
                        <p className="mt-1">Enter the current 6-digit code from your authenticator app.</p>
                      </div>
                      <div>
                        <label className="block text-xs font-medium uppercase tracking-[0.08em] text-slate-500">
                          Authenticator code
                        </label>
                        <input
                          type="text"
                          value={setupCode}
                          onChange={(event) => setSetupCode(event.target.value)}
                          placeholder="Enter 6-digit code"
                          className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100"
                        />
                      </div>
                      <button
                        onClick={confirmSetup}
                        disabled={loading}
                        className="inline-flex w-full items-center justify-center rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-900 transition-colors hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-60 md:w-auto"
                      >
                        Confirm and Enable MFA
                      </button>
                      <p className="text-xs text-slate-500">
                        If the code fails, wait for the next 30-second refresh and try again.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {isEnabled && (
              <div className="mt-5 rounded-xl border border-slate-200 bg-white p-4">
                <p className="text-sm font-semibold text-slate-900">Manage MFA</p>
                <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
                  <label className="block text-xs font-medium uppercase tracking-[0.08em] text-slate-500">
                    Current MFA code
                  </label>
                  <input
                    type="text"
                    value={actionCode}
                    onChange={(event) => setActionCode(event.target.value)}
                    placeholder="Enter TOTP or recovery code"
                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100"
                  />
                  <div className="mt-3 grid gap-2 sm:grid-cols-2">
                    <button
                      onClick={regenerateRecoveryCodes}
                      disabled={loading}
                      className="inline-flex items-center justify-center rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm font-semibold text-indigo-900 transition-colors hover:bg-indigo-100 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      Regenerate Recovery Codes
                    </button>
                    <button
                      onClick={disableMfa}
                      disabled={loading}
                      className="inline-flex items-center justify-center rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm font-semibold text-rose-900 transition-colors hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      Disable MFA
                    </button>
                  </div>
                </div>
              </div>
            )}

            {recoveryCodes.length > 0 && (
              <div className="mt-5 rounded-xl border border-amber-200 bg-amber-50 p-4">
                <p className="text-sm font-semibold text-amber-900">Recovery codes</p>
                <p className="mt-1 text-xs text-amber-800">
                  Save these now. Each code can be used only once.
                </p>
                <div className="mt-3 grid gap-2 sm:grid-cols-2">
                  {recoveryCodes.map((code) => (
                    <code
                      key={code}
                      className="rounded-md border border-amber-200 bg-white px-2 py-1.5 text-sm text-amber-900"
                    >
                      {code}
                    </code>
                  ))}
                </div>
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};
